import { inject } from '@angular/core';
import { PlanService } from '../services/plan.service';
import { Router, CanActivateFn, CanActivateChildFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PermissionService } from '../services/permission.service';
import { EmployeeAuthService } from '../services/employee-auth.service';
import { map, timeout, catchError, filter, take } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { permisosDePantalla, moduloDePantalla } from '../config/permisos-pantallas';

/**
 * Guard para proteger rutas basándose en permisos específicos
 * 
 * Uso en las rutas:
 * {
 *   path: 'configuracion',
 *   canActivate: [permissionGuard('configuracion.ver')],
 *   component: ConfiguracionComponent
 * }
 * 
 * Para múltiples permisos (requiere todos):
 * canActivate: [permissionGuard(['usuarios.ver', 'usuarios.crear'])]
 * 
 * Para múltiples permisos (requiere al menos uno):
 * canActivate: [permissionGuardAny(['usuarios.ver', 'roles.ver'])]
 */
export function permissionGuard(permission: string | string[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const permissionService = inject(PermissionService);
    const employeeAuth = inject(EmployeeAuthService);
    const router = inject(Router);

    // Verificar si está autenticado
    if (!employeeAuth.isAuthenticated()) {
      console.log('❌ [PermissionGuard] No autenticado, redirigiendo a login');
      return router.createUrlTree(['/employee/login'], {
        queryParams: { returnUrl: state.url }
      });
    }

    console.log('🔐 [PermissionGuard] Esperando a que el empleado esté cargado...');

    // Esperar a que el empleado esté cargado (con timeout de 5 segundos)
    return employeeAuth.currentEmployee$.pipe(
      filter(employee => employee !== null), // Esperar a que NO sea null
      take(1), // Tomar solo el primer valor válido
      timeout(5000), // Timeout de 5 segundos
      map(employee => {
        console.log('👤 [PermissionGuard] Empleado cargado:', employee?.nombre);
        
        // Verificar permisos
        const permissions = Array.isArray(permission) ? permission : [permission];
        const hasPermission = permissionService.hasAllPermissions(permissions);

        if (!hasPermission) {
          console.log('❌ [PermissionGuard] Acceso denegado:', {
            empleado: employee.nombre,
            permisosRequeridos: permissions,
            permisosActuales: permissionService.getAllPermissions(),
            esOwner: employee.is_owner,
            roles: employee.roles?.map(r => r.name)
          });
          
          return router.createUrlTree(['/app/dashboard'], {
            queryParams: { accessDenied: true }
          });
        }

        console.log('✅ [PermissionGuard] Permiso verificado:', permissions);
        return true;
      }),
      catchError(error => {
        console.error('❌ [PermissionGuard] Error o timeout esperando empleado:', error);
        // Si hay timeout o error, redirigir a login
        return of(router.createUrlTree(['/employee/login'], {
          queryParams: { returnUrl: state.url }
        }));
      })
    );
  };
}

/**
 * Guard para proteger rutas requiriendo al menos uno de los permisos especificados
 * 
 * Uso en las rutas:
 * {
 *   path: 'reportes',
 *   canActivate: [permissionGuardAny(['informes.ver', 'administracion.ver'])],
 *   component: ReportesComponent
 * }
 */
export function permissionGuardAny(permissions: string[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const permissionService = inject(PermissionService);
    const employeeAuth = inject(EmployeeAuthService);
    const router = inject(Router);

    if (!employeeAuth.isAuthenticated()) {
      console.log('❌ [PermissionGuardAny] No autenticado');
      return router.createUrlTree(['/employee/login'], {
        queryParams: { returnUrl: state.url }
      });
    }

    console.log('🔐 [PermissionGuardAny] Esperando empleado...');

    return employeeAuth.currentEmployee$.pipe(
      filter(employee => employee !== null),
      take(1),
      timeout(5000),
      map(employee => {
        console.log('👤 [PermissionGuardAny] Empleado:', employee?.nombre);

        const hasAnyPermission = permissionService.hasAnyPermission(permissions);

        if (!hasAnyPermission) {
          console.log('❌ [PermissionGuardAny] Acceso denegado:', {
            empleado: employee.nombre,
            permisosRequeridos: permissions
          });
          
          return router.createUrlTree(['/app/dashboard'], {
            queryParams: { accessDenied: true }
          });
        }

        console.log('✅ [PermissionGuardAny] Permiso verificado');
        return true;
      }),
      catchError(error => {
        console.error('❌ [PermissionGuardAny] Error:', error);
        return of(router.createUrlTree(['/employee/login'], {
          queryParams: { returnUrl: state.url }
        }));
      })
    );
  };
}

/**
 * Guard para proteger rutas solo para owners
 */
export function ownerOnlyGuard(): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const permissionService = inject(PermissionService);
    const employeeAuth = inject(EmployeeAuthService);
    const router = inject(Router);

    if (!employeeAuth.isAuthenticated()) {
      console.log('❌ [OwnerOnlyGuard] No autenticado');
      return router.createUrlTree(['/employee/login'], {
        queryParams: { returnUrl: state.url }
      });
    }

    console.log('🔐 [OwnerOnlyGuard] Esperando empleado...');

    return employeeAuth.currentEmployee$.pipe(
      filter(employee => employee !== null),
      take(1),
      timeout(5000),
      map(employee => {
        console.log('👤 [OwnerOnlyGuard] Empleado:', employee?.nombre);

        if (!permissionService.isOwner()) {
          console.log('❌ [OwnerOnlyGuard] No es owner');
          return router.createUrlTree(['/app/dashboard'], {
            queryParams: { accessDenied: true, ownerOnly: true }
          });
        }

        console.log('✅ [OwnerOnlyGuard] Owner verificado');
        return true;
      }),
      catchError(error => {
        console.error('❌ [OwnerOnlyGuard] Error:', error);
        return of(router.createUrlTree(['/employee/login'], {
          queryParams: { returnUrl: state.url }
        }));
      })
    );
  };
}

/**
 * Guard único del app operativo: busca la pantalla en PERMISOS_PANTALLAS y exige
 * alguno de sus permisos. Va como canActivateChild en la ruta `app`, así cubre
 * cualquier pantalla —también las que se abren tipeando la URL— sin repetir el
 * permiso en cada ruta. Las que no están en la tabla pasan sin pedir nada extra.
 */
export const pantallaGuard: CanActivateChildFn = (_route, state) => {
  // Módulo fuera del plan: se explica en vez de abrir una pantalla que el servidor
  // va a rechazar. Si el plan todavía no cargó, pasa (el servidor corta igual).
  const modulo = moduloDePantalla(state.url);
  // Si el negocio lo usó con un plan mayor, entra a consultar lo que cargó.
  const planService = inject(PlanService);
  if (modulo && !planService.incluye(modulo) && !planService.soloLectura(modulo)) {
    return inject(Router).createUrlTree(['/app/plan-no-incluye'], { queryParams: { modulo } });
  }

  const permisos = permisosDePantalla(state.url);
  if (!permisos) {
    return true;
  }

  const permissionService = inject(PermissionService);
  const employeeAuth = inject(EmployeeAuthService);
  const router = inject(Router);

  return employeeAuth.currentEmployee$.pipe(
    filter(employee => employee !== null),
    take(1),
    timeout(5000),
    map(() => permissionService.hasAnyPermission(permisos)
      ? true
      : router.createUrlTree(['/app/dashboard'], { queryParams: { accessDenied: true } })),
    catchError(() => of(router.createUrlTree(['/employee/login'], { queryParams: { returnUrl: state.url } })))
  );
};
